import { useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { api } from './lib/api'
import type { CategoryDef, User } from './lib/api'
import { AppShell } from './components/AppShell'
import { Spinner } from './components/bits'
import { Dashboard } from './pages/Dashboard'
import { Sessions } from './pages/Sessions'
import { SessionDetail } from './pages/SessionDetail'
import { SetupForm } from './pages/SetupForm'
import { Garage } from './pages/Garage'
import { Login } from './pages/Login'
import { SetPassword } from './pages/SetPassword'
import { Settings } from './pages/Settings'
import { EventDetail, Events } from './pages/Events'
import { Compare } from './pages/Compare'
import { Tune } from './pages/Tune'
import {
  NotFound, TrackDetail, Tracks,
} from './pages/misc'

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means signed out, not a transient failure - retrying it just
      // delays showing the login screen.
      retry: (count, error: any) => error?.status !== 401 && count < 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

function Shell({ user }: { user: User }) {
  const garage = useQuery({
    queryKey: ['garage'],
    queryFn: () => api.get<{ categories: CategoryDef[] }>('/api/garage'),
  })

  return (
    <AppShell user={user} garageCategories={garage.data?.categories ?? []}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/sessions" element={<Sessions />} />
        <Route path="/sessions/:id" element={<SessionDetail />} />
        <Route path="/sessions/:id/setup" element={<SetupForm />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/tracks" element={<Tracks />} />
        <Route path="/tracks/:slug" element={<TrackDetail user={user} />} />
        <Route path="/garage" element={<Garage />} />
        <Route path="/garage/:category" element={<Garage />} />
        <Route path="/weather" element={<Tune />} />
        <Route path="/compare" element={<Compare user={user} />} />
        <Route path="/settings" element={<Settings user={user} />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  )
}

function Root() {
  const [user, setUser] = useState<User | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    api.get<{ authenticated: boolean; user?: User }>('/api/auth/me')
      .then((b) => setUser(b.authenticated ? b.user! : null))
      .catch(() => setUser(null))
      .finally(() => setChecked(true))
  }, [])

  // The invite link is reached before signing in, so it bypasses the gate.
  if (window.location.pathname === '/set-password') return <SetPassword />

  if (!checked) return <Spinner />
  if (!user) return <Login onSignedIn={setUser} />
  return <BrowserRouter><Shell user={user} /></BrowserRouter>
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <Root />
    </QueryClientProvider>
  )
}
